import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query'
import { iamClient } from '@/api/iam'
import { sdk, createAuth } from '@/api/sdk'
import { useAuth } from '@/contexts/auth-context'
import { ROLES } from '@/lib/constants'

export interface ProjectInfo {
  id: string
  code: string
  name: string
  apiKey?: string
}

interface ProjectContextValue {
  project: ProjectInfo | null
  projects: ProjectInfo[]
  isLoading: boolean
  selectProject: (projectId: string) => Promise<void>
  billingActiveProjects: Record<string, boolean> | null
  refreshBillingStatus: () => void
  iamRegisteredProjects: Set<string> | null
}

const ProjectContext = createContext<ProjectContextValue | null>(null)

interface ProjectSelection {
  owner: string
  projectId: string
}

function toProjectInfo(p: Record<string, unknown>): ProjectInfo {
  return {
    id: p.id as string,
    code: (p.code as string) || '',
    name: (p.name as string) || '',
    apiKey: p.apiKey as string | undefined,
  }
}

export function resolveProjectId(
  projects: ProjectInfo[],
  identityProjectId?: string | null,
  selectedProjectId?: string | null,
): string | null {
  if (selectedProjectId && projects.some((project) => project.id === selectedProjectId)) return selectedProjectId
  if (identityProjectId && projects.some((project) => project.id === identityProjectId)) return identityProjectId
  return projects[0]?.id ?? null
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, getToken } = useAuth()
  const isSuperAdmin = user?.roles?.some((r) => r.name === ROLES.SUPER_ADMIN) ?? false
  const allowedProjectIds = user?.projectIds ?? []
  const queryClient = useQueryClient()
  const identityKey = isAuthenticated ? user?.id || user?.email || null : null
  const [selection, setSelection] = useState<ProjectSelection | null>(null)

  // Fetch all available projects (filtered for non-super-admin)
  const projectsQuery = useQuery({
    queryKey: ['projects', identityKey, isSuperAdmin, allowedProjectIds],
    queryFn: async () => {
      const result = (await iamClient.projects.search({ keyword: '', pageable: { pageNumber: 0, pageSize: 500 } })) as Record<string, unknown>
      const data = result.projects as { content: Record<string, unknown>[] } | undefined
      if (!data?.content) return []
      const all = data.content.filter((p) => !!p.id).map(toProjectInfo)
      if (isSuperAdmin) return all
      const allowed = new Set(allowedProjectIds)
      return all.filter((p) => allowed.has(p.id))
    },
    enabled: isAuthenticated && !!identityKey,
  })

  const personaIdsQuery = useQuery({
    queryKey: ['persona-project-ids', identityKey],
    queryFn: async () => {
      const token = await getToken()
      const client = sdk.projects(createAuth(token))
      const ids = new Set<string>()
      const size = 200
      let page = 0
      let total = Infinity
      while (page * size < total && page < 50) {
        const result = await client.list(null, page, size)
        for (const item of result.items) if (item.id) ids.add(item.id)
        total = result.total
        page += 1
      }
      return ids
    },
    enabled: isAuthenticated && !!identityKey,
    staleTime: 5 * 60_000,
  })

  const iamProjects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data])
  const projects = useMemo(() => {
    if (!personaIdsQuery.isSuccess) return iamProjects
    const personaIds = personaIdsQuery.data
    return iamProjects.filter((candidate) => personaIds.has(candidate.id))
  }, [iamProjects, personaIdsQuery.isSuccess, personaIdsQuery.data])

  const selectedId = projectsQuery.isSuccess
    ? resolveProjectId(
        projects,
        user?.projectId,
        selection?.owner === identityKey ? selection.projectId : null,
      )
    : null

  const projectIds = useMemo(() => projects.map((p) => p.id), [projects])

  // One query per project, same key as ProjectSubscriptionSheet so the cache is shared.
  // When the sheet activates billing and invalidates ['project-subscription', id],
  // this automatically picks up the change with no extra wiring.
  const billingQueries = useQueries({
    queries: projectIds.map((id) => ({
      queryKey: ['project-subscription', id],
      queryFn: async () => {
        const token = await getToken()
        return sdk.projects(createAuth(token)).getSubscription(id)
      },
      enabled: isAuthenticated && personaIdsQuery.isSuccess,
      staleTime: 60_000,
    })),
  })

  const billingActiveProjects = useMemo(() => {
    if (projectIds.length === 0) return null
    // Return null while any project's subscription is still being fetched for the first time
    // so we don't flash false "No billing" badges during initial load.
    if (billingQueries.some((q) => q.isPending)) return null
    const result: Record<string, boolean> = {}
    billingQueries.forEach((q, i) => {
      result[projectIds[i]] = !!q.data
    })
    return result
  }, [billingQueries, projectIds])

  const refreshBillingStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['project-subscription'] })
    queryClient.invalidateQueries({ queryKey: ['persona-project-ids'] })
  }, [queryClient])

  const userId = user?.id
  const selectProject = useCallback(
    async (projectId: string) => {
      if (!userId || !identityKey) throw new Error('Cannot select a project without an authenticated user')
      if (!projects.some((project) => project.id === projectId)) {
        throw new Error('Cannot select a project outside the authenticated user membership')
      }

      await iamClient.users.setProject({ userId, projectId })
      await queryClient.cancelQueries()
      await queryClient.resetQueries({
        predicate: (query) => query.queryKey[0] !== 'projects',
      })
      setSelection({ owner: identityKey, projectId })
    },
    [identityKey, projects, queryClient, userId],
  )

  const project =
    isAuthenticated && projectsQuery.isSuccess
      ? projects.find((candidate) => candidate.id === selectedId) ?? null
      : null
  const isLoading = isAuthenticated && (identityKey ? projectsQuery.isPending : true)

  return (
    <ProjectContext.Provider
      value={{
        project,
        projects,
        isLoading,
        selectProject,
        billingActiveProjects,
        refreshBillingStatus,
        iamRegisteredProjects: projectsQuery.isLoading
          ? null
          : new Set(iamProjects.map((p) => p.id)),
      }}
    >
      {children}
    </ProjectContext.Provider>
  )
}

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext)
  if (!ctx) throw new Error('useProject must be used within ProjectProvider')
  return ctx
}
